/**
 * The one way an exercise entry is summarised, used by the coach's plan editor
 * and by the client's session screen.
 *
 * It exists because there were two of them. `EntryLine` and `ExerciseCard` built
 * the same string from the same fields in two places, and both had drifted from
 * the scenario in `mvp-discovery`'s `training-plans` spec, which says "3 × 10"
 * where the build said "3 sets · 10 reps". Nobody noticed for the whole of
 * `foundation-slice`. Two call sites is how that happens, so there is now one
 * (`exercise-entry-formats` design.md E5).
 *
 * The rule the whole thing follows is E1: **normalise what is a number, render
 * verbatim what is words.** `rest_seconds` is a quantity the product stores, so
 * we choose its unit on the way out. `reps` and `load` are the coach's own
 * language — "8-10 @ RPE 7", "30 s hold", "bodyweight" — and are passed through
 * character for character, never reformatted, rounded or converted.
 */

/** What the formatter needs. A whole `ExerciseEntryRow` satisfies it. */
export interface ExerciseLineParts {
  sets: number | null;
  reps: string | null;
  load: string | null;
  rest_seconds: number | null;
}

/** The separator between the parts of the line. */
const SEPARATOR = ' · ';

/**
 * Rest in the unit a person would say it in (E6): seconds below two minutes,
 * minutes at or above it, with any remaining seconds included.
 *
 * `90` → `90 s`, `120` → `2 min`, `150` → `2 min 30 s`. The threshold is where
 * a coach switches vocabulary; below it "120 s" reads as arithmetic rather than
 * as an instruction.
 */
export function formatRestSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const whole = Math.round(seconds);
  if (whole < 120) return `${String(whole)} s`;

  const minutes = Math.floor(whole / 60);
  const remainder = whole % 60;
  return remainder === 0
    ? `${String(minutes)} min`
    : `${String(minutes)} min ${String(remainder)} s`;
}

/**
 * Sets and reps as a coach writes them: `3 × 8-10`, `3 ×` never appearing
 * without its reps and reps never appearing with a bare number in front of them.
 *
 * Sets alone is "3 sets", because "3" on its own says nothing.
 */
function formatSetsAndReps(sets: number | null, reps: string | null): string | null {
  const trimmedReps = reps?.trim() ?? '';
  const hasSets = sets !== null && sets > 0;

  if (hasSets && trimmedReps !== '') return `${String(sets)} × ${trimmedReps}`;
  if (hasSets) return `${String(sets)} sets`;
  if (trimmedReps !== '') return trimmedReps;
  return null;
}

/**
 * Everything about an entry except its name, joined with `·`.
 *
 * Every part the coach left empty is omitted entirely — no gap, no zero, no
 * stray label — so an entry with only reps reads `5 min` and nothing else.
 */
export function formatExerciseDetail(entry: ExerciseLineParts): string {
  const rest = entry.rest_seconds === null ? '' : formatRestSeconds(entry.rest_seconds);

  return [
    formatSetsAndReps(entry.sets, entry.reps),
    entry.load?.trim() ?? '',
    rest === '' ? '' : `rest ${rest}`,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(SEPARATOR);
}

/**
 * The whole line, name included: `Goblet squat · 3 × 8-10 · 16 kg · rest 90 s`.
 * An entry with nothing but a name is just the name.
 */
export function formatExerciseLine(entry: ExerciseLineParts & { name: string }): string {
  const detail = formatExerciseDetail(entry);
  return detail === '' ? entry.name : `${entry.name}${SEPARATOR}${detail}`;
}
